import type {
    CSSProperties,
    MouseEvent as ReactMouseEvent,
    PointerEvent as ReactPointerEvent
} from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'

import {
    ChevronLeft,
    ChevronRight,
    Pause,
    Play,
    Star,
    X
} from 'lucide-react'

/* Images */
const UNSPLASH_PHOTO_IDS = [
    '1506905925346-21bda4d32df4',
    '1469474968028-56623f02e42e',
    '1518837695005-2083093ee35b',
    '1441974231531-c6227db76b6e',
    '1470071459604-3b5ec3a7fe05',
    '1447752875215-b2761acb3c5d',
    '1500964757637-c85e8a162699',
    '1454496522488-7a8e488e8606',
    '1501785888041-af3ef285b470',
    '1444080748397-f442aa95c3e5',
    '1431794062232-2a99a5431c6c',
    '1426604966848-d7adac402bff',
    '1490604001847-b712b0c2f967',
    '1497436072909-60f360e1d4b1',
    '1505144808419-1957a94ca61e',
    '1505765050516-f72dcac9c60e',
    '1542273917363-3b1817f69a2d',
    '1418065460487-3e41a6c84dc5',
    '1502082553048-f009c37129b9',
    '1483347756197-71ef80e95f73',
    '1485470733090-0aae1788d5af',
    '1495107334309-fcf20504a5ab',
    '1472213984618-c79aaec7fef0',
    '1480714378408-67cf0d13bc1b',
    '1449034446853-66c86144b0ad',
    '1506744038136-46273834b3fb',
    '1519681393784-d120267933ba',
    '1500530855697-b586d89ba3ee',
    '1469854523086-cc02fe5d8800',
    '1488972685288-c3fd157d7c7a',
    '1518173946687-a4c8892bbd9f',
    '1444723121867-7a241cacace9'
]

const thumb = (id: string) =>
    `https://images.unsplash.com/photo-${id}?w=420&h=560&fit=crop&q=70&auto=format`

const full = (id: string) =>
    `https://images.unsplash.com/photo-${id}?w=900&h=1200&fit=crop&q=75&auto=format`

const UNIQUE = UNSPLASH_PHOTO_IDS.length
const REPEATS = 4
const N = UNIQUE * REPEATS

const CELLS = Array.from({ length: N }, (_, i) => UNSPLASH_PHOTO_IDS[i % UNIQUE])

/* Layout */
const BASE_W = 46
const BASE_H = 61
const GAP = 6
const STEP = BASE_W + GAP
const TOTAL = N * STEP

/* Quality scaling */
const QUALITY = 5
const RENDER_W = BASE_W * QUALITY
const RENDER_H = BASE_H * QUALITY
const REST_SCALE = 1 / QUALITY

/* Physics */
const FRICTION = 0.94
const WHEEL_GAIN = 0.6
const DRAG_GAIN = 1.45
const AUTO_DRIFT = -0.32

/* Stage window */
const STAGE_START = 0.3
const STAGE_END = 0.8
const STAGE_SPAN = 0.5

/* Left-side curve */
const CURVE_END = 0.55
const CURVE_LIFT = 220

/* Deterministic chaos seeds */
function hash(n: number) {
    const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453
    return x - Math.floor(x)
}

function halton(index: number, base: number) {
    let f = 1
    let r = 0
    let n = index + 1

    while (n > 0) {
        f /= base
        r += f * (n % base)
        n = Math.floor(n / base)
    }

    return r
}

interface Chaos {
    cx: number
    cy: number
    rot: number
    scale: number
    yBias: number
}

const CHAOS: Chaos[] = Array.from({ length: N }, (_, i) => ({
    cx: halton(i, 2) * 2 - 1,
    cy: halton(i, 3) * 2 - 1,
    rot: (hash(i * 3 + 3) - 0.5) * 26,
    scale: 2.4 + hash(i * 5 + 4) * 1.9,
    yBias: (hash(i * 7 + 9) - 0.5) * 18
}))

const INITIAL_SWEEP =
    'linear-gradient(to right, black 0%, black 0%, transparent 32%, transparent 100%)'

const VIGNETTE =
    'radial-gradient(ellipse 78% 82% at center, black 10%, transparent 92%)'

const STRIP_MASK =
    'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)'

const TITLE_LINES = ['Every frame,', 'every story.']

function splitChars(line: string, lineIndex: number) {
    return line.split('').map((ch, i) => (
        <span
            key={`${lineIndex}-${i}`}
            data-char
            className='inline-block whitespace-pre will-change-transform'
        >
            {ch === ' ' ? ' ' : ch}
        </span>
    ))
}

export const PortfolioReel = () => {
    const sectionRef = useRef<HTMLElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)
    const cellRefs = useRef<(HTMLDivElement | null)[]>([])
    const lightboxBoxRef = useRef<HTMLDivElement>(null)
    const rafRef = useRef<number | null>(null)

    /* RAF state (refs avoid re-renders) */
    const scrollRef = useRef(0)
    const velRef = useRef(0)
    const scrollTargetRef = useRef<number | null>(null)
    const draggingRef = useRef(false)
    const dragRef = useRef({ startX: 0, lastX: 0, didMove: false, startScroll: 0 })
    const animRef = useRef({ chaosEnable: 0, reveal: 0 })
    const revealingRef = useRef(true)
    const dimsRef = useRef({ vw: 1280, vh: 800 })
    const mobileRef = useRef(false)
    const lightboxAccumRef = useRef(0)
    const sourceRectRef = useRef<DOMRect | null>(null)
    const openIndexRef = useRef<number | null>(null)
    const playingRef = useRef(true)

    const [dims, setDims] = useState({ vw: 1280, vh: 800 })
    const [lensM, setLensM] = useState(1)
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const [isPlaying, setIsPlaying] = useState(true)

    /* Track viewport */
    useEffect(() => {
        const update = () => {
            const vw = window.innerWidth
            const vh = window.innerHeight
            dimsRef.current = { vw, vh }
            mobileRef.current = vw < 768
            setDims({ vw, vh })
        }

        update()
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])

    /* Wireframe grid with magnifying-glass distortion */
    const grid = useMemo(() => {
        const { vw, vh } = dims
        const isMobile = vw < 768
        const lensCx = vw / 2
        const lensCy = isMobile ? vh * 0.4 : vh / 2
        const lensR = isMobile ? 204 : 336
        const M = lensM

        const mag = (d: number) => {
            if (d >= lensR) return 1
            const k = 1 - d / lensR
            return 1 + (M - 1) * k * k
        }

        const distort = (x: number, y: number): [number, number] => {
            const dx = x - lensCx
            const dy = y - lensCy
            const d = Math.hypot(dx, dy)
            const m = mag(d)
            return [lensCx + dx * m, lensCy + dy * m]
        }

        const vlines: string[] = []
        for (let x = 0; x <= vw; x += BASE_W) {
            const pts: string[] = []
            for (let y = 0; y <= vh; y += 6) {
                const [px, py] = distort(x, y)
                pts.push(`${px.toFixed(1)},${py.toFixed(1)}`)
            }
            vlines.push(pts.join(' '))
        }

        const hlines: string[] = []
        for (let y = 0; y <= vh; y += BASE_H) {
            const pts: string[] = []
            for (let x = 0; x <= vw; x += 6) {
                const [px, py] = distort(x, y)
                pts.push(`${px.toFixed(1)},${py.toFixed(1)}`)
            }
            hlines.push(pts.join(' '))
        }

        return { vlines, hlines }
    }, [dims, lensM])

    /* Entrance animations */
    useEffect(() => {
        const ctx = gsap.context(() => {
            const svg = svgRef.current
            if (svg) {
                const sweep = { p: 0 }
                gsap.to(sweep, {
                    p: 100,
                    duration: 1.8,
                    delay: 0.1,
                    ease: 'power3.inOut',
                    onUpdate: () => {
                        const a = sweep.p
                        const b = Math.min(100, sweep.p + 32)
                        const grad = `linear-gradient(to right, black 0%, black ${a}%, transparent ${b}%, transparent 100%)`
                        svg.style.maskImage = grad
                        svg.style.webkitMaskImage = grad
                    }
                })
            }

            gsap.to(animRef.current, {
                reveal: 1,
                duration: 1.8,
                delay: 0.4,
                ease: 'power3.inOut',
                onComplete: () => {
                    revealingRef.current = false
                }
            })

            gsap.to(animRef.current, {
                chaosEnable: 1,
                duration: 1.6,
                delay: 1.9,
                ease: 'power2.inOut'
            })

            const lens = { v: 1 }
            gsap.to(lens, {
                v: 2.4,
                duration: 1.6,
                delay: 1.9,
                ease: 'power2.inOut',
                onUpdate: () => setLensM(lens.v)
            })

            const tl = gsap.timeline({ delay: 0.25 })
            tl.from('[data-eyebrow]', {
                opacity: 0,
                y: 22,
                filter: 'blur(10px)',
                duration: 0.8,
                ease: 'power3.out'
            })
            tl.from(
                '[data-char]',
                {
                    opacity: 0,
                    y: 28,
                    scale: 0.7,
                    filter: 'blur(14px)',
                    duration: 0.9,
                    ease: 'power4.out',
                    stagger: 0.028
                },
                '-=0.55'
            )
            tl.from(
                '[data-sub]',
                { opacity: 0, y: 22, filter: 'blur(10px)', duration: 0.8, ease: 'power3.out' },
                '-=0.55'
            )
            tl.from(
                '[data-cta]',
                { opacity: 0, y: 22, filter: 'blur(10px)', duration: 0.8, ease: 'power3.out' },
                '-=0.55'
            )
            tl.from(
                '[data-trust]',
                { opacity: 0, y: 22, filter: 'blur(10px)', duration: 0.8, ease: 'power3.out' },
                '-=0.55'
            )
        }, sectionRef)

        return () => ctx.revert()
    }, [])

    /* Animation loop */
    useEffect(() => {
        const loop = () => {
            const { vw, vh } = dimsRef.current
            const isMobile = mobileRef.current
            const lbOpen = openIndexRef.current !== null
            const playing = playingRef.current

            if (scrollTargetRef.current !== null) {
                const target = scrollTargetRef.current
                const delta = target - scrollRef.current
                scrollRef.current += delta * 0.18
                if (Math.abs(delta) < 0.3) {
                    scrollRef.current = target
                    scrollTargetRef.current = null
                    scrollRef.current = ((scrollRef.current % TOTAL) + TOTAL) % TOTAL
                }
            } else if (lbOpen && playing) {
                scrollRef.current += velRef.current + AUTO_DRIFT
                velRef.current *= FRICTION
                scrollRef.current = ((scrollRef.current % TOTAL) + TOTAL) % TOTAL
                lightboxAccumRef.current += velRef.current + AUTO_DRIFT

                if (lightboxAccumRef.current <= -STEP) {
                    const cur = openIndexRef.current
                    if (cur !== null) {
                        const next = (cur + 1) % N
                        openIndexRef.current = next
                        setOpenIndex(next)
                    }
                    lightboxAccumRef.current += STEP
                } else if (lightboxAccumRef.current >= STEP) {
                    const cur = openIndexRef.current
                    if (cur !== null) {
                        const next = (cur - 1 + N) % N
                        openIndexRef.current = next
                        setOpenIndex(next)
                    }
                    lightboxAccumRef.current -= STEP
                }
            } else if (lbOpen && !playing) {
                /* strip frozen */
            } else if (!draggingRef.current) {
                if (isMobile) {
                    velRef.current = Math.max(-7, Math.min(7, velRef.current))
                }
                scrollRef.current += velRef.current + AUTO_DRIFT
                velRef.current *= isMobile ? 0.82 : FRICTION
                scrollRef.current = ((scrollRef.current % TOTAL) + TOTAL) % TOTAL
            }

            const scroll = scrollRef.current
            const ce = animRef.current.chaosEnable
            const reveal = animRef.current.reveal
            const revealing = revealingRef.current
            const cy = isMobile ? vh * 0.4 : vh / 2
            const SPREAD_X = vw * (isMobile ? 0.24 : 0.22)
            const SPREAD_Y = vh * (isMobile ? 0.22 : 0.3)

            for (let i = 0; i < N; i++) {
                const el = cellRefs.current[i]
                if (!el) continue
                const c = CHAOS[i]

                let x = i * STEP - scroll
                x = ((x % TOTAL) + TOTAL) % TOTAL
                if (x > vw + STEP) x -= TOTAL

                const cxNorm = (x + BASE_W / 2) / vw

                let t = 0
                if (ce > 0 && cxNorm > STAGE_START && cxNorm < STAGE_END) {
                    const p = (cxNorm - STAGE_START) / STAGE_SPAN
                    t = Math.sin(p * Math.PI)
                    t = t * t * (3 - 2 * t)
                    t *= ce
                }

                const dx = c.cx * SPREAD_X * t
                const dy = isMobile
                    ? (c.cy * 0.6 - 0.45) * SPREAD_Y * t
                    : c.cy * SPREAD_Y * t
                const rot = c.rot * t
                const scale = REST_SCALE * (1 + (c.scale - 1) * t)

                let curveLift = 0
                if (cxNorm < CURVE_END) {
                    const k = 1 - cxNorm / CURVE_END
                    const eased = k * k * (3 - 2 * k)
                    curveLift = -CURVE_LIFT * eased
                }

                const tx = x + BASE_W / 2 - RENDER_W / 2 + dx
                const ty = cy - RENDER_H / 2 + c.yBias + dy + curveLift

                el.style.transform = `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg) scale(${scale})`
                el.style.zIndex = String(1 + Math.round(t * 30))

                if (revealing) {
                    const local = (reveal - cxNorm) * 7
                    el.style.opacity = String(Math.max(0, Math.min(1, local)))
                } else {
                    el.style.opacity = '1'
                }
            }

            rafRef.current = requestAnimationFrame(loop)
        }

        rafRef.current = requestAnimationFrame(loop)
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [])

    /* Wheel input (non-passive so we can take over scroll) */
    useEffect(() => {
        const sec = sectionRef.current
        if (!sec) return

        const onWheel = (e: WheelEvent) => {
            e.preventDefault()
            scrollTargetRef.current = null
            velRef.current += e.deltaY * WHEEL_GAIN * 0.05
        }

        sec.addEventListener('wheel', onWheel, { passive: false })
        return () => sec.removeEventListener('wheel', onWheel)
    }, [])

    /* Drag input (window listeners catch releases outside the section) */
    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            if (!draggingRef.current) return
            const d = dragRef.current
            const mobile = mobileRef.current
            let delta = e.clientX - d.lastX
            if (mobile) delta = Math.max(-9, Math.min(9, delta))
            const gain = mobile ? 0.3 : DRAG_GAIN
            scrollRef.current -= delta * gain
            velRef.current = -delta * gain
            d.lastX = e.clientX
            if (Math.abs(e.clientX - d.startX) > 4) d.didMove = true
        }

        const onUp = () => {
            if (!draggingRef.current) return
            draggingRef.current = false
            scrollRef.current = ((scrollRef.current % TOTAL) + TOTAL) % TOTAL
        }

        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
        }
    }, [])

    /* Lightbox FLIP from the clicked thumbnail */
    useEffect(() => {
        if (openIndex === null) return
        const box = lightboxBoxRef.current
        const src = sourceRectRef.current
        if (!box || !src) return

        const dst = box.getBoundingClientRect()
        const sx = src.width / dst.width
        const sy = src.height / dst.height
        const tx = src.left + src.width / 2 - (dst.left + dst.width / 2)
        const ty = src.top + src.height / 2 - (dst.top + dst.height / 2)

        gsap.fromTo(
            box,
            { x: tx, y: ty, scaleX: sx, scaleY: sy, opacity: 0.6 },
            { x: 0, y: 0, scaleX: 1, scaleY: 1, opacity: 1, duration: 0.7, ease: 'expo.out' }
        )

        sourceRectRef.current = null
    }, [openIndex])

    /* Keyboard + body scroll lock while open */
    useEffect(() => {
        if (openIndex === null) return

        const onKey = (e: KeyboardEvent) => {
            if (e.repeat) return
            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                advance(-1)
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                advance(1)
            } else if (e.key === ' ') {
                e.preventDefault()
                togglePlay()
            } else if (e.key === 'Escape') {
                e.preventDefault()
                close()
            }
        }

        window.addEventListener('keydown', onKey)
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', onKey)
            document.body.style.overflow = ''
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openIndex])

    /* Actions */
    const openLightbox = (i: number, el: HTMLElement) => {
        sourceRectRef.current = el.getBoundingClientRect()
        openIndexRef.current = i
        setOpenIndex(i)
        playingRef.current = true
        setIsPlaying(true)
        velRef.current = 0
        lightboxAccumRef.current = 0
    }

    const advance = (dir: number) => {
        const cur = openIndexRef.current
        if (cur === null) return
        const next = (cur + dir + N) % N
        openIndexRef.current = next
        setOpenIndex(next)
        lightboxAccumRef.current = 0
        scrollTargetRef.current = scrollRef.current + dir * STEP
    }

    const togglePlay = () => {
        playingRef.current = !playingRef.current
        setIsPlaying(playingRef.current)
    }

    const close = () => {
        openIndexRef.current = null
        setOpenIndex(null)
    }

    /* Strip interactions */
    const onSectionPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
        if (openIndexRef.current !== null) return
        draggingRef.current = true
        scrollTargetRef.current = null
        velRef.current = 0
        dragRef.current = {
            startX: e.clientX,
            lastX: e.clientX,
            didMove: false,
            startScroll: scrollRef.current
        }
    }

    const onCellClick = (i: number, e: ReactMouseEvent<HTMLDivElement>) => {
        if (dragRef.current.didMove) return
        openLightbox(i, e.currentTarget)
    }

    const onCellEnter = (e: ReactPointerEvent<HTMLDivElement>) => {
        if (mobileRef.current) return
        const inner = e.currentTarget.querySelector<HTMLElement>('[data-tilt]')
        if (inner) inner.style.transition = 'transform 0.5s cubic-bezier(0.16,1,0.3,1)'
    }

    const onCellMove = (e: ReactPointerEvent<HTMLDivElement>) => {
        if (mobileRef.current) return
        const cell = e.currentTarget
        const inner = cell.querySelector<HTMLElement>('[data-tilt]')
        const sheen = cell.querySelector<HTMLElement>('[data-sheen]')
        const rect = cell.getBoundingClientRect()
        const u = (e.clientX - rect.left) / rect.width - 0.5
        const v = (e.clientY - rect.top) / rect.height - 0.5

        if (inner) {
            inner.style.transition = 'transform 0.08s linear'
            inner.style.transform = `perspective(520px) rotateY(${u * 32}deg) rotateX(${-v * 32}deg) scale(1.12)`
        }
        if (sheen) {
            sheen.style.opacity = '1'
            sheen.style.background = `radial-gradient(circle at ${(u + 0.5) * 100}% ${(v + 0.5) * 100}%, rgba(255,255,255,0.55), rgba(255,255,255,0) 55%)`
        }
    }

    const onCellLeave = (e: ReactPointerEvent<HTMLDivElement>) => {
        const inner = e.currentTarget.querySelector<HTMLElement>('[data-tilt]')
        const sheen = e.currentTarget.querySelector<HTMLElement>('[data-sheen]')
        if (inner) {
            inner.style.transition = 'transform 0.7s cubic-bezier(0.16,1,0.3,1)'
            inner.style.transform = ''
        }
        if (sheen) sheen.style.opacity = '0'
    }

    const cellStyle: CSSProperties = {
        width: RENDER_W,
        height: RENDER_H,
        opacity: 0,
        transformOrigin: 'center',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'transform'
    }

    const lightboxBoxStyle: CSSProperties = {
        width: 'min(78vw, calc(82vh * 3 / 4))',
        height: 'min(82vh, calc(78vw * 4 / 3))'
    }

    const renderLightbox = (idx: number) => {
        const id = UNSPLASH_PHOTO_IDS[idx % UNIQUE]
        const label = `Selected photograph ${(idx % UNIQUE) + 1}`

        return (
            <div className='fixed inset-0 z-[60] flex items-center justify-center'>
                <div
                    className='absolute inset-0 bg-black/35 backdrop-blur-[2px]'
                    onClick={close}
                    aria-hidden='true'
                />

                <button
                    type='button'
                    onClick={() => advance(-1)}
                    aria-label='Previous photo'
                    className='absolute left-4 top-1/2 z-[61] -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20'
                >
                    <ChevronLeft className='size-6' />
                </button>

                <button
                    type='button'
                    onClick={() => advance(1)}
                    aria-label='Next photo'
                    className='absolute right-4 top-1/2 z-[61] -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20'
                >
                    <ChevronRight className='size-6' />
                </button>

                <button
                    type='button'
                    onClick={close}
                    aria-label='Close'
                    className='absolute right-6 top-6 z-[61] rounded-full bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20'
                >
                    <X className='size-5' />
                </button>

                <div
                    ref={lightboxBoxRef}
                    className='relative z-[61] overflow-hidden rounded-lg bg-black shadow-2xl'
                    style={lightboxBoxStyle}
                >
                    <img
                        src={full(id)}
                        alt={label}
                        draggable={false}
                        className='h-full w-full object-cover'
                    />

                    <button
                        type='button'
                        onClick={togglePlay}
                        aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                        className='absolute right-3 top-3 rounded-full bg-black/55 p-2 text-white backdrop-blur transition hover:bg-black/75'
                    >
                        {isPlaying ? <Pause className='size-4' /> : <Play className='size-4' />}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <section
            ref={sectionRef}
            aria-label='Photography portfolio reel'
            onPointerDown={onSectionPointerDown}
            className='relative isolate h-screen w-full cursor-grab touch-none select-none overflow-hidden bg-background active:cursor-grabbing'
        >
            {/* Wireframe grid + vignette */}
            <div
                className='pointer-events-none absolute inset-0 z-0'
                style={{ maskImage: VIGNETTE, WebkitMaskImage: VIGNETTE }}
                aria-hidden='true'
            >
                <svg
                    ref={svgRef}
                    width={dims.vw}
                    height={dims.vh}
                    viewBox={`0 0 ${dims.vw} ${dims.vh}`}
                    preserveAspectRatio='none'
                    className='absolute inset-0 h-full w-full text-foreground'
                    style={{ maskImage: INITIAL_SWEEP, WebkitMaskImage: INITIAL_SWEEP }}
                >
                    {grid.vlines.map((pts, i) => (
                        <polyline
                            key={`v${i}`}
                            points={pts}
                            fill='none'
                            stroke='currentColor'
                            strokeOpacity='0.12'
                            strokeWidth='1'
                        />
                    ))}
                    {grid.hlines.map((pts, i) => (
                        <polyline
                            key={`h${i}`}
                            points={pts}
                            fill='none'
                            stroke='currentColor'
                            strokeOpacity='0.12'
                            strokeWidth='1'
                        />
                    ))}
                </svg>
            </div>

            {/* Strip */}
            <div
                className='absolute inset-0 z-10'
                style={{ maskImage: STRIP_MASK, WebkitMaskImage: STRIP_MASK }}
            >
                {CELLS.map((id, i) => (
                    <div
                        key={i}
                        ref={(el) => {
                            cellRefs.current[i] = el
                        }}
                        onClick={(e) => onCellClick(i, e)}
                        onPointerEnter={onCellEnter}
                        onPointerMove={onCellMove}
                        onPointerLeave={onCellLeave}
                        className='group absolute left-0 top-0 overflow-hidden rounded-[10px]'
                        style={cellStyle}
                    >
                        <div
                            data-tilt
                            className='absolute inset-0'
                            style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
                        >
                            <img
                                src={thumb(id)}
                                alt={`Portfolio photograph ${(i % UNIQUE) + 1}`}
                                loading='lazy'
                                draggable={false}
                                className='h-full w-full scale-[1.05] object-cover'
                            />
                            <div
                                data-sheen
                                className='pointer-events-none absolute inset-0 opacity-0 mix-blend-overlay'
                                style={{ transition: 'opacity 0.4s ease-out' }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Hero copy */}
            <div className='pointer-events-none absolute bottom-6 left-5 right-5 z-[40] md:bottom-14 md:left-14 md:right-auto md:max-w-2xl'>
                <div
                    data-eyebrow
                    className='mb-5 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-foreground/70'
                >
                    <span className='h-px w-8 bg-foreground/60' />
                    <span>Selected work · 2014 — 2026</span>
                </div>

                <h1 className='font-[family-name:var(--font-display)] text-[2.25rem] font-semibold leading-[0.95] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl'>
                    <span className='block'>{splitChars(TITLE_LINES[0], 0)}</span>
                    <span className='block italic'>{splitChars(TITLE_LINES[1], 1)}</span>
                </h1>

                <p
                    data-sub
                    className='mt-6 hidden max-w-md text-base leading-relaxed text-foreground/65 md:block'
                >
                    Twelve years of editorial, fashion, and brand photography — one infinite reel.
                    Hover to feel it; click to see it full.
                </p>

                <div data-cta className='mt-7 flex flex-wrap items-center gap-4'>
                    <a
                        href='#book'
                        className='pointer-events-auto inline-flex items-center rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition hover:opacity-90'
                    >
                        Book a session
                    </a>
                    <a
                        href='#archive'
                        className='pointer-events-auto inline-flex items-center text-sm font-medium text-foreground/80 underline-offset-4 transition hover:text-foreground hover:underline'
                    >
                        View archive
                    </a>
                </div>

                <div
                    data-trust
                    className='mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground/60'
                >
                    <span className='flex items-center gap-0.5 text-foreground'>
                        {Array.from({ length: 5 }, (_, i) => (
                            <Star key={i} className='size-3.5 fill-current' strokeWidth={0} />
                        ))}
                    </span>
                    <span className='font-semibold text-foreground'>4.9</span>
                    <span aria-hidden='true'>·</span>
                    <span>240+ commissioned shoots</span>
                    <span className='hidden sm:inline' aria-hidden='true'>·</span>
                    <span className='hidden sm:inline'>Featured in Vogue · WIRED · Apple</span>
                </div>
            </div>

            {/* Lightbox */}
            {openIndex !== null ? renderLightbox(openIndex) : null}
        </section>
    )
}
